import { formatDkkPrice } from '../../lib/format.ts';
import { EYEBROW, BUTTON_FILL, type ThemeClasses } from './cmsTheme.ts';
import type { TicketGroupView, TicketGroupName } from '../../domain/ticketGroups.ts';

export interface TicketGroupsListProps {
  readonly groups: readonly TicketGroupView[];
  /** The section color combination. The whole box follows it: the panel is `theme.card`, a
   *  shade of the section background rather than a contrasting brand fill. */
  readonly theme: ThemeClasses;
  /** Where each box purchase button points. Defaults to the on-page ticket anchor. */
  readonly buyUrl?: string;
}

/** Per-group purchase label: "Køb billet" / "Køb glamping" / "Køb overnatning". */
const BUY_LABELS: Record<TicketGroupName, string> = {
  Adgangsbilletter: 'Køb billet',
  Glamping: 'Køb glamping',
  Overnatning: 'Køb overnatning',
};

/**
 * The grouped ticket list, used by CmsTicketsSection (all groups when no categories are selected,
 * otherwise only the chosen groups). Each group is a panel in the section card shade: a heading, a
 * column header, one hairline row per ticket with the price right-aligned, and a purchase button.
 *
 * v2 replaced the dotted leaders with plain hairline rows — a price list is a table, and the
 * leaders were drawing more attention than the prices. Sold-out rows stay in place at 55%
 * opacity rather than being called out in accent color: they are absence of an option, not news.
 */
export function TicketGroupsList({ groups, theme, buyUrl = '#billetter' }: TicketGroupsListProps) {
  const card = theme.card;
  const buyClasses = BUTTON_FILL[theme.button];
  // The configured United Tickets storefront leaves the site, so open it in a new tab;
  // the on-page fallback anchor should keep the current tab.
  const external = !buyUrl.startsWith('#');
  return (
    <div className="space-y-6 md:space-y-8">
      {groups.map(({ group, tickets }) => (
        <div key={group} className={`${card.bg} ${card.text} rounded-card p-6 md:p-9`}>
          <h2 className={`font-display text-3xl md:text-[40px] leading-none tracking-[-0.02em] ${card.text} mb-6 md:mb-8`}>
            {group}
          </h2>

          <div className={`flex items-baseline justify-between gap-4 pb-2 ${EYEBROW} ${card.muted}`}>
            <span>Type</span>
            <span>Pris DKK ekskl. gebyr</span>
          </div>

          <ul>
            {tickets.map((ticket, i) => (
              <li
                key={`${ticket.name}-${i}`}
                className={`flex items-baseline justify-between gap-4 py-3 border-t ${card.border}${ticket.SoldOut ? ' opacity-[0.55]' : ''}`}
              >
                <span className={`font-medium text-sm md:text-base ${card.text}`}>{ticket.name}</span>
                {ticket.SoldOut ? (
                  <span className={`whitespace-nowrap text-sm md:text-base font-medium ${card.text}`}>
                    Udsolgt
                  </span>
                ) : (
                  <span className={`whitespace-nowrap text-sm md:text-base tabular-nums ${card.text}`}>
                    {formatDkkPrice(ticket.price)}
                  </span>
                )}
              </li>
            ))}
          </ul>

          <div className="mt-7 md:mt-8">
            <a
              href={buyUrl}
              target={external ? '_blank' : undefined}
              rel={external ? 'noopener noreferrer' : undefined}
              className={`inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium transition-colors ${buyClasses}`}
            >
              <span aria-hidden="true">🎟</span> {BUY_LABELS[group]}
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}
